import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchLevels,
  fetchOrCreateSettings,
  setSelectedLevel,
  clearErrors
} from '../notificationSlice';

import NotificationSettingsPanel from './NotificationSettingsPanel';
import NotificationHistoryPanel from './NotificationHistoryPanel';
import NotificationQueuePanel from './NotificationQueuePanel';
import TemplateSettingsPanel from './TemplateSettingsPanel';
import SendOnAPIPanel from './SendOnAPIPanel';
import LoadingOverlay from '../../ui/LoadingOverlay';

const NotificationManagementPage = () => {
  const dispatch = useDispatch();
  const { levels, selectedLevel, loading, error } = useSelector(state => state.notification);
  const [activeTab, setActiveTab] = useState('settings');

  useEffect(() => {
    dispatch(fetchLevels());
    return () => {
      dispatch(clearErrors());
    };
  }, [dispatch]);

  useEffect(() => {
    if (levels.length > 0 && !selectedLevel) {
      dispatch(setSelectedLevel(null));
      dispatch(fetchOrCreateSettings(null));
    }
  }, [levels, selectedLevel, dispatch]);

  const handleLevelSelect = (levelId) => {
    dispatch(setSelectedLevel(levelId));
    dispatch(fetchOrCreateSettings(levelId));
  };

  const tabs = [
    { id: 'settings', label: '알림 설정', icon: '⚙️' },
    { id: 'templates', label: '템플릿 설정', icon: '📄' },
    { id: 'queue', label: '대기열', icon: '⏰' },
    { id: 'history', label: '발송 내역', icon: '📋' },
    { id: 'api', label: 'API 설정', icon: '🔑' }
  ];

  if (loading.levels) {
    return <LoadingOverlay />;
  }

  return (
    <div className="notification-management-page">
      <div className="page-header">
        <h1>알림톡 관리 (베타)</h1>
        <p>학생과 학부모에게 보낼 알림톡을 설정하고 관리합니다.</p>
      </div>

      <div className="notification-content">
        {/* Level Selection */}
        <div className="level-selector">
          <label htmlFor="level-select">반 선택:</label>
          <select 
            id="level-select"
            value={selectedLevel || ''}
            onChange={(e) => handleLevelSelect(e.target.value || null)}
            className="level-select"
          >
            <option value="">기본 설정 (전체)</option>
            {levels.map(level => (
              <option key={level.id} value={level.id}>
                {level.name}
              </option>
            ))}
          </select>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'settings' && (
            <div className="settings-tab-wrapper">
              <NotificationSettingsPanel />
            </div>
          )}
          {activeTab === 'templates' && <TemplateSettingsPanel />}
          {activeTab === 'queue' && <NotificationQueuePanel />}
          {activeTab === 'history' && <NotificationHistoryPanel />}
          {activeTab === 'api' && <SendOnAPIPanel />}
        </div>
      </div>

      {/* Error Display */}
      {error.levels && (
        <div className="error-message">
          반 정보를 불러오는데 실패했습니다: {typeof error.levels === 'string' ? error.levels : JSON.stringify(error.levels)}
        </div>
      )}

      <style jsx>{`
        .notification-management-page {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .page-header {
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 2px solid #e0e0e0;
        }

        .page-header h1 {
          font-size: 28px;
          margin: 0 0 8px 0;
          color: #333;
        }

        .page-header p {
          font-size: 16px;
          color: #666;
          margin: 0;
        }

        .notification-content {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .level-selector {
          padding: 20px;
          background: #f8f9fa;
          border-bottom: 1px solid #e0e0e0;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .level-selector label {
          font-weight: 600;
          color: #333;
          min-width: 80px;
        }

        .level-select {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          background: white;
          min-width: 300px;
        }

        .level-select:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }

        .tab-navigation {
          display: flex;
          background: #f8f9fa;
          border-bottom: 1px solid #e0e0e0;
        }

        .tab-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 16px 24px;
          border: none;
          background: transparent;
          cursor: pointer;
          font-size: 14px;
          color: #666;
          transition: all 0.2s;
          border-bottom: 3px solid transparent;
        }

        .tab-button:hover {
          background: rgba(0, 123, 255, 0.05);
          color: #007bff;
        }

        .tab-button.active {
          background: white;
          color: #007bff;
          border-bottom-color: #007bff;
        }

        .tab-icon {
          font-size: 16px;
        }

        .tab-label {
          font-weight: 500;
        }

        .tab-content {
          min-height: 400px;
        }

        .settings-tab-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 500px;
        }

        .error-message {
          margin-top: 20px;
          padding: 12px 16px;
          background: #ffebee;
          border: 1px solid #ffcdd2;
          border-radius: 4px;
          color: #c62828;
          font-size: 14px;
        }
      `}</style>
    </div>
  );
};

export default NotificationManagementPage;
